import React, { useEffect, useState } from 'react'
import {
  Card, Descriptions, Tag, Button, Space, message, Typography, Tabs, Table,
  Steps, Timeline, Spin,
} from 'antd'
import {
  ArrowLeftOutlined, SendOutlined, ShoppingCartOutlined, InboxOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { purchaseApi } from '../../services/api'

const { Title } = Typography

const STATUS_COLORS = {
  '草稿': 'default', '审批中': 'processing', '已驳回': 'error',
  '已下单': 'blue', '已到货': 'cyan', '已完成': 'green',
}

export default function PurchaseDetail() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [purchase, setPurchase] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const res = await purchaseApi.get(id)
      setPurchase(res)
    } catch {
      message.error('加载采购详情失败')
      navigate('/purchases')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDetail()
  }, [id])

  const handleAction = async (action) => {
    try {
      if (action === 'submit') {
        await purchaseApi.submit(id)
        message.success('已提交审批')
      } else if (action === 'order') {
        await purchaseApi.order(id)
        message.success('已确认下单')
      } else if (action === 'arrive') {
        await purchaseApi.arrive(id)
        message.success('已确认到货，入库单已自动生成')
      }
      fetchDetail()
    } catch (e) {
      message.error(e?.error || '操作失败')
    }
  }

  const renderActions = () => {
    if (!purchase) return null
    const { status } = purchase
    const buttons = []

    if (status === '草稿' || status === '已驳回') {
      buttons.push(
        <Button key="submit" type="primary" icon={<SendOutlined />} onClick={() => handleAction('submit')}>
          提交审批
        </Button>
      )
    }
    if (status === '审批中') {
      buttons.push(
        <Button key="order" type="primary" icon={<ShoppingCartOutlined />} onClick={() => handleAction('order')}>
          确认下单
        </Button>
      )
    }
    if (status === '已下单') {
      buttons.push(
        <Button key="arrive" type="primary" icon={<InboxOutlined />} onClick={() => handleAction('arrive')}>
          确认到货
        </Button>
      )
    }

    return buttons
  }

  const itemColumns = [
    { title: '产品', key: 'product', render: (_, r) => `${r.product?.sku || ''} ${r.product?.name || ''}` },
    { title: '数量', dataIndex: 'quantity', key: 'quantity' },
    { title: '进价', dataIndex: 'unitPrice', key: 'unitPrice', render: (v) => v ? `¥${Number(v).toLocaleString()}` : '—' },
    {
      title: '小计', key: 'subtotal',
      render: (_, r) => `¥${((r.unitPrice || 0) * r.quantity).toLocaleString()}`,
    },
    { title: '期望到货', dataIndex: 'expectDate', key: 'expectDate', render: (v) => v ? new Date(v).toLocaleDateString('zh-CN') : '—' },
    { title: '备注', dataIndex: 'notes', key: 'notes', render: (v) => v || '—' },
  ]

  const stockInColumns = [
    { title: '产品', dataIndex: ['product', 'name'], key: 'product' },
    { title: '数量', dataIndex: 'quantity', key: 'quantity' },
    { title: '进价', dataIndex: 'unitPrice', key: 'unitPrice', render: (v) => v ? `¥${Number(v).toLocaleString()}` : '—' },
    { title: '入库时间', dataIndex: 'createdAt', key: 'createdAt', render: (v) => new Date(v).toLocaleString('zh-CN') },
  ]

  if (loading) return <Spin style={{ display: 'block', marginTop: 100 }} />

  const wf = purchase.workflowInstance
  const stages = wf?.template?.stages ? JSON.parse(wf.template.stages) : []

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchases')}>返回</Button>
        <Title level={4} style={{ margin: 0 }}>采购详情 - {purchase.code}</Title>
        <Tag color={STATUS_COLORS[purchase.status]}>{purchase.status}</Tag>
      </Space>

      <Space style={{ marginBottom: 16 }}>
        {renderActions()}
      </Space>

      <Tabs defaultActiveKey="info" items={[
        {
          key: 'info',
          label: '基本信息',
          children: (
            <>
              <Card style={{ marginBottom: 16 }}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="采购编号">{purchase.code}</Descriptions.Item>
                  <Descriptions.Item label="供应商">{purchase.supplier?.name || '未指定'}</Descriptions.Item>
                  <Descriptions.Item label="采购来源">
                    <Tag>{purchase.source}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="关联编号">{purchase.sourceId || '—'}</Descriptions.Item>
                  <Descriptions.Item label="申请人">{purchase.user?.name || '—'}</Descriptions.Item>
                  <Descriptions.Item label="总额">¥{Number(purchase.totalAmount).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="创建时间">{new Date(purchase.createdAt).toLocaleString('zh-CN')}</Descriptions.Item>
                  <Descriptions.Item label="更新时间">{new Date(purchase.updatedAt).toLocaleString('zh-CN')}</Descriptions.Item>
                  <Descriptions.Item label="备注" span={2}>{purchase.notes || '—'}</Descriptions.Item>
                </Descriptions>
              </Card>

              <Card title="采购明细">
                <Table
                  columns={itemColumns}
                  dataSource={purchase.items || []}
                  rowKey="id"
                  pagination={false}
                  summary={() => (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={1}><strong>合计</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{purchase.items?.reduce((s, i) => s + i.quantity, 0) || 0}</Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3}>
                        <strong style={{ color: '#f5222d' }}>¥{Number(purchase.totalAmount).toLocaleString()}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={2} />
                    </Table.Summary.Row>
                  )}
                />
              </Card>
            </>
          ),
        },
        {
          key: 'workflow',
          label: '工作流进度',
          children: (
            <Card title="审批进度">
              {wf ? (
                <>
                  <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="工作流状态">
                      <Tag color={wf.status === 'completed' ? 'green' : wf.status === 'active' ? 'processing' : 'error'}>
                        {wf.status === 'active' ? '进行中' : wf.status === 'completed' ? '已完成' : '已驳回'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="当前阶段">第 {wf.currentStage} 阶段</Descriptions.Item>
                  </Descriptions>

                  <Steps
                    current={wf.currentStage - 1}
                    status={wf.status === 'rejected' ? 'error' : 'process'}
                    items={stages.map((s) => ({ title: s.name, description: s.type === 'approval' ? `需${s.role === 'admin' ? '管理员' : '主管'}审批` : '' }))}
                  />

                  {wf.actions?.length > 0 && (
                    <div style={{ marginTop: 24 }}>
                      <Title level={5}>审批记录</Title>
                      <Timeline
                        items={wf.actions.map((a) => ({
                          color: a.action === 'approve' ? 'green' : a.action === 'reject' ? 'red' : 'blue',
                          children: (
                            <div>
                              <strong>{a.user?.name || '系统'}</strong>
                              {' '}
                              <Tag color={a.action === 'approve' ? 'green' : a.action === 'reject' ? 'red' : 'default'}>
                                {a.action === 'approve' ? '通过' : a.action === 'reject' ? '驳回' : a.action}
                              </Tag>
                              {a.comment && <div style={{ color: '#666' }}>{a.comment}</div>}
                              <div style={{ fontSize: 12, color: '#999' }}>{new Date(a.createdAt).toLocaleString('zh-CN')}</div>
                            </div>
                          ),
                        }))}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#999' }}>尚未启动审批流程</div>
              )}
            </Card>
          ),
        },
        {
          key: 'stockins',
          label: '入库记录',
          children: (
            <Card title="关联入库单">
              {purchase.stockIns?.length > 0 ? (
                <Table
                  columns={stockInColumns}
                  dataSource={purchase.stockIns}
                  rowKey="id"
                  pagination={false}
                />
              ) : (
                <div style={{ color: '#999' }}>暂无入库记录</div>
              )}
            </Card>
          ),
        },
      ]} />
    </div>
  )
}
